import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import "./App.css";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";

// Dashboard Pages
import SuperAdminDashboard from "./pages/dashboards/SuperAdminDashboard";
import HRAdminDashboard from "./pages/dashboards/HRAdminDashboard";
import ManagerDashboard from "./pages/dashboards/ManagerDashboard";
import EmployeeDashboard from "./pages/dashboards/EmployeeDashboard";

// Tenant Pages
import TenantsPage from "./pages/tenants/TenantsPage";
import TenantDetailPage from "./pages/tenants/TenantDetailPage";
import SubscriptionPlansPage from "./pages/subscriptions/SubscriptionPlansPage";

// User Pages
import UsersPage from "./pages/users/UsersPage";

// Org Pages
import DepartmentsPage from "./pages/organization/DepartmentsPage";
import DesignationsPage from "./pages/organization/DesignationsPage";

// Attendance Pages
import AttendancePage from "./pages/attendance/AttendancePage";
import AttendanceReportPage from "./pages/attendance/AttendanceReportPage";

// Leave Pages
import LeavesPage from "./pages/leaves/LeavesPage";
import LeaveBalancePage from "./pages/leaves/LeaveBalancePage";
import LeavePoliciesPage from "./pages/leaves/LeavePoliciesPage";

// Document Pages
import DocumentsPage from "./pages/documents/DocumentsPage";

// Salary Pages
import SalaryStructuresPage from "./pages/salary/SalaryStructuresPage";
import SalarySlipsPage from "./pages/salary/SalarySlipsPage";

// Notice Pages
import NoticesPage from "./pages/notices/NoticesPage";

// Resignation Pages
import ResignationsPage from "./pages/resignations/ResignationsPage";

// Settings Pages
import ProfilePage from "./pages/settings/ProfilePage";
import NotificationsPage from "./pages/settings/NotificationsPage";
import AuditLogsPage from "./pages/settings/AuditLogsPage";
import HolidaysPage from "./pages/settings/HolidaysPage";

// Geo Location Pages
import GeoLocationsPage from "./pages/locations/GeoLocationsPage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Dashboard Router - redirects based on role
const DashboardRouter = () => {
  const { user } = useAuth();

  switch (user?.role) {
    case "super_admin":
      return <SuperAdminDashboard />;
    case "hr_admin":
      return <HRAdminDashboard />;
    case "manager":
      return <ManagerDashboard />;
    default:
      return <EmployeeDashboard />;
  }
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />}
      />
      <Route
        path="/register"
        element={<Navigate to="/login" />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardRouter />} />

        {/* Tenant Routes - SuperAdmin Only */}
        <Route
          path="tenants"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <TenantsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="tenants/:id"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <TenantDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="subscription-plans"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <SubscriptionPlansPage />
            </ProtectedRoute>
          }
        />

        {/* User Routes */}
        <Route
          path="users"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <UsersPage />
            </ProtectedRoute>
          }
        />

        {/* Organization Routes */}
        <Route
          path="departments"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <DepartmentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="designations"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <DesignationsPage />
            </ProtectedRoute>
          }
        />

        {/* Attendance Routes */}
        <Route path="attendance" element={<AttendancePage />} />
        <Route
          path="attendance/reports"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin", "manager"]}>
              <AttendanceReportPage />
            </ProtectedRoute>
          }
        />

        {/* Leave Routes */}
        <Route path="leaves" element={<LeavesPage />} />
        <Route path="leaves/balance" element={<LeaveBalancePage />} />
        <Route
          path="leaves/policies"
          element={
            <ProtectedRoute allowedRoles={["hr_admin"]}>
              <LeavePoliciesPage />
            </ProtectedRoute>
          }
        />

        {/* Document Routes */}
        <Route path="documents" element={<DocumentsPage />} />

        {/* Holidays & Calendar Routes */}
        <Route
          path="holidays"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <HolidaysPage />
            </ProtectedRoute>
          }
        />

        {/* Salary Routes */}
        <Route
          path="salary/structures"
          element={
            <ProtectedRoute allowedRoles={["hr_admin"]}>
              <SalaryStructuresPage />
            </ProtectedRoute>
          }
        />
        <Route path="salary/slips" element={<SalarySlipsPage />} />

        {/* Notice Routes */}
        <Route path="notices" element={<NoticesPage />} />

        {/* Resignation Routes */}
        <Route path="resignations" element={<ResignationsPage />} />

        {/* Geo Location Routes */}
        <Route
          path="locations"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <GeoLocationsPage />
            </ProtectedRoute>
          }
        />

        {/* Settings Routes */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route
          path="audit-logs"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "hr_admin"]}>
              <AuditLogsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
